<template>
  <rv-step-sidebar
    ref="dialogRef"
    custom-back-event
    data-automation="edit-member-sidebar"
    :hide-back-btn="hideBackButton"
    :hide-close-btn="hideCloseButton"
    :no-esc-dismiss="hideCloseButton"
    @on-back="onBackClick"
  >
    <rv-step-sidebar-content
      class="member-detail-sidebar"
      data-automation="edit-member-sidebar__content"
      :loading="loading"
      :title="title"
      @hide="onHide"
    >
      <div class="q-mx-lg q-mb-lg flex column items-center justify-center">
        <rv-avatar
          :background-color="v$.email.$invalid ? 'grey-6' : undefined"
          class="relative-position q-mb-sm"
          data-automation="edit-member-sidebar__avatar"
          :icon="v$.email.$invalid ? 'person' : undefined"
          :label="v$.email.$invalid ? undefined : email"
          size="xl"
          :text-color="v$.email.$invalid ? 'grey-2' : undefined"
          :uuid="member ? member.id : email"
        />
        <!-- <rv-button
          data-automation="edit-member-sidebar__upload-picture"
          icon="rvai-base:camera"
          :label="$t('members.actions.uploadPicture')"
          secondary
          size="sm"
          @click="onUploadPicture"
        /> -->
        <!-- <div class="q-mt-sm">
          <span class="text-small q-mr-sm">{{ $t('members.titles.format') }}</span>
          <span class="text-small">{{ $t('members.titles.maxSize', [2]) }}</span>
        </div> -->
      </div>

      <!-- @vue-ignore -->
      <rv-input
        v-model="email"
        bottom-slots
        class="q-mx-lg q-mb-lg"
        data-automation="edit-member-sidebar__email"
        :disable="isEditing"
        :hint="isEditing ? undefined : $t('members.titles.johnDoe')"
        :label="$t('members.titles.email')"
        :validation="v$.email"
      />

      <div
        v-if="isEditing"
        class="q-mx-lg q-mb-lg"
      >
        <div class="text-subtitle3">
          {{ $t('members.titles.password') }}
        </div>
        <rv-button
          v-if="isCurrentProfileEditing"
          class="full-width"
          color="primary"
          data-automation="edit-member-sidebar__change-password"
          :label="$t('members.actions.changePassword')"
          secondary
          @click="onChangePassword"
        />
        <rv-button
          v-else
          class="full-width"
          color="primary"
          data-automation="edit-member-sidebar__reset-password"
          :disable="passwordResetLoading"
          :label="$t('members.actions.resetMembersPassword')"
          :loading="passwordResetLoading"
          secondary
          @click="onPasswordReset"
        />
      </div>

      <!-- @vue-ignore -->
      <rv-select
        v-model="role"
        class="q-mb-lg q-mx-lg"
        data-automation="edit-member-sidebar__role-select"
        :disable="isCurrentProfileEditing"
        :label="$t('members.titles.memberRole')"
        map-options
        option-label="name"
        option-value="id"
        :options="roles"
        :validation="v$.role"
      />

      <rv-expansion-item
        v-if="permissions"
        border
        class="q-mb-lg q-mx-lg"
        data-automation="edit-member-sidebar__permissions"
        :default-opened="!isCurrentProfileEditing"
        :label="$t('roles.permissionsTitle')"
      >
        <div
          v-for="(permission, index) in permissions"
          :key="index"
          class="row no-wrap flex items-center q-px-sm q-pb-sm"
        >
          <span class="q-pr-sm">{{ $t('general.symbols.separator') }}</span>
          <rv-ellipsis-text
            :data-automation="`edit-member-sidebar__permission--${permission}`"
            :text="$t(`roles.permissions.${permission}.name`)"
          />
          <q-icon
            v-if="$t(`roles.permissions.${permission}.description`)"
            class="info-icon q-pl-sm"
            color="black-1"
            name="info"
            size="16px"
          >
            <rv-tooltip data-automation="info-icon__tooltip">
              {{ $t(`roles.permissions.${permission}.description`) }}
            </rv-tooltip>
          </q-icon>
        </div>
      </rv-expansion-item>

      <template v-if="isCurrentProfileEditing">
        <p
          class="text-subtitle3 q-my-md q-mx-lg"
          data-automation="edit-member-sidebar__appearance"
        >
          {{ $t('about.about.appearance.title') }}
        </p>

        <DarkModeSwitch class="justify-center q-my-md" />
      </template>

      <template #actions>
        <rv-button
          v-if="isCreating"
          data-automation="edit-member-sidebar__create-member"
          :disable="v$.$invalid || !email"
          :label="$t('members.titles.createMember')"
          @click="onCreateMember"
        />

        <template v-if="isEditing">
          <rv-button
            color="primary"
            data-automation="edit-member-sidebar__cancel"
            :label="isCurrentProfileEditing ? $t('general.actions.close') : $t('general.actions.cancel')"
            :secondary="!isCurrentProfileEditing"
            @click="onCancel"
          />

          <rv-button
            v-if="!isCurrentProfileEditing"
            color="primary"
            data-automation="edit-member-sidebar__edit-member"
            :disable="v$.$invalid || !isDirty"
            :label="$t('general.actions.save')"
            @click="onEditMember"
          />
        </template>
      </template>
    </rv-step-sidebar-content>
  </rv-step-sidebar>
</template>

<script lang="ts" setup>
import {
  provideStepSidebar,
  useSidebar,
  ValidationBuilder,
} from '@robovision/rvai-base'
import { HttpStatusCode } from '@robovision/rvai-utils'
import useVuelidate from '@vuelidate/core'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import {
  computed,
  onMounted,
  type PropType,
  ref,
} from 'vue'
import { useI18n } from 'vue-i18n'
import DarkModeSwitch from '~components/DarkModeSwitch.vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import type { Member } from '~domain/Member'
import type { MemberRole } from '~domain/MemberRole'
import { useMemberRolesStore } from '~stores/useMemberRolesStore'
import { useMembersStore } from '~stores/useMembersStore'
import { useSessionStore } from '~stores/useSessionStore'
import { isApiError } from '~utils/api'
import { EditMemberSidebarSteps } from './EditMemberSidebarSteps'
import StepChangePasswordOld from './StepChangePasswordOld.vue'
import StepMailInvitation from './StepMailInvitation.vue'


const props = defineProps({
  member: {
    default: undefined,
    type: Object as PropType<Member>,
  },
})

const $q = useQuasar()
const { t } = useI18n()

const {
  currentStep,
  onBack,
  onReset,
  pushStep,
} = provideStepSidebar()
const {
  dialogRef,
  onCancel,
  onHide,
  onOK,
} = useSidebar()

const sessionStore = useSessionStore()
const { currentUser } = storeToRefs(sessionStore)

const membersStore = useMembersStore()
const { members } = storeToRefs(membersStore)
const {
  inviteUser,
  regenerateMemberInvitationToken,
  updateMember,
} = membersStore

const memberRolesStore = useMemberRolesStore()
const { roles } = storeToRefs(memberRolesStore)
const { loadMembersRoles } = memberRolesStore

const createdMember = ref<Member | null>(null)
const email = ref(props.member?.email ?? '')
const loading = ref(false)
const passwordResetLoading = ref(false)
const role = ref<MemberRole | null>(props.member?.role ?? null)
const updatedMember = ref<Member | undefined>(props.member)

const isDirty = computed(
  () => (props.member?.email ?? '') !== email.value ||
    (props.member?.role ?? null) !== role.value,
)

const hideBackButton = computed(() => {
  if (!currentStep) return true
  switch (currentStep.value?.stepName) {
    case EditMemberSidebarSteps.MailInvitation:
    case EditMemberSidebarSteps.PasswordSaved:
      return true
    default:
      return false
  }
})

const hideCloseButton = computed(() => {
  if (!currentStep) return false
  switch (currentStep.value?.stepName) {
    case EditMemberSidebarSteps.ChangePasswordNew:
    case EditMemberSidebarSteps.ChangePasswordOld:
      return true
    default:
      return false
  }
})

const isCreating = computed(() => !props.member)
const isCurrentProfileEditing = computed(() => isEditing.value &&
  currentUser.value && currentUser.value.id === props.member?.id)
const isEditing = computed(() => !!props.member)
const permissions = computed(() => role.value?.permissions)

const title = computed(() => {
  if (isCreating.value) return t('members.titles.createMember')
  return isCurrentProfileEditing.value ? t('members.titles.yourProfile') : t('members.titles.editMember')
})

const existingEmails = computed(() => {
  return members.value
    .map(u => u.email)
    .filter(e => !props.member || e !== props.member.email)
})
const validationRules = computed(() => ({
  email: ValidationBuilder
    .$t('members.titles.email')
    .email
    .uniqueValue(existingEmails)
    .rules,
  role: ValidationBuilder
    .$t('members.titles.memberRole')
    .required
    .rules,
}))
const v$ = useVuelidate(validationRules, {
  email,
  role,
}, { $autoDirty: true })

function onBackClick (): void {
  if (currentStep.value?.stepName === EditMemberSidebarSteps.ChangePasswordNew) {
    onReset()
  } else {
    onBack()
  }
}

function onChangePassword (): void {
  onGoToStep(EditMemberSidebarSteps.ChangePasswordOld)
}

async function onCreateMember (): Promise<void> {
  if (!role.value) return

  try {
    loading.value = true
    createdMember.value = await inviteUser({
      email: email.value,
      roleId: role.value?.id,
    })

    onGoToStep(EditMemberSidebarSteps.MailInvitation)
  } catch (e) {
    if (isApiError(e, { statusCode: HttpStatusCode.BAD_REQUEST })) {
      $q.notify({
        message: t('validation.email'),
        type: 'negative',
      })
    }
    console.error(e)
  } finally {
    loading.value = false
  }
}

function onGoToStep (step: EditMemberSidebarSteps): void {
  switch (step) {
    case EditMemberSidebarSteps.ChangePasswordOld:
      return pushStep({
        component: StepChangePasswordOld,
        componentProps: {
          member: props.member,
        },
        stepName: EditMemberSidebarSteps.ChangePasswordOld,
      })
    case EditMemberSidebarSteps.MailInvitation:
      return pushStep({
        component: StepMailInvitation,
        componentProps: {
          isCreating: isCreating.value,
          member: isCreating.value ? createdMember.value : updatedMember.value,
        },
        stepName: EditMemberSidebarSteps.MailInvitation,
      })
  }
}

async function onEditMember (): Promise<void> {
  if (!updatedMember.value || !role.value) return

  try {
    loading.value = true
    await updateMember(updatedMember.value.id, role.value.id)

    onOK()
  } catch (e) {
    if (isApiError(e, {
      id: ApiErrorId.LastAdminNotAllowed,
      statusCode: HttpStatusCode.CONFLICT,
    })) {
      $q.notify({
        message: t('members.errors.editLastAdmin'),
        type: 'negative',
      })
    } else {
      $q.notify({
        message: t('members.errors.edit'),
        type: 'negative',
      })
    }
  } finally {
    loading.value = false
  }
}

async function onPasswordReset (): Promise<void> {
  if (!updatedMember.value) return

  try {
    passwordResetLoading.value = true
    updatedMember.value = await regenerateMemberInvitationToken(updatedMember.value.id)
  } catch (error) {
    console.error(error)
    $q.notify({
      message: t('members.errors.generateNewInviteToken'),
      type: 'negative',
    })
  } finally {
    passwordResetLoading.value = false
  }

  onGoToStep(EditMemberSidebarSteps.MailInvitation)
}

// function onUploadPicture (): void {
//   console.log('upload picture')
// }

onMounted(async () => {
  try {
    if (!isCurrentProfileEditing.value) {
      await loadMembersRoles()
    }

    // TODO remove once we have actual roles
    if (!role.value) {
      role.value = roles.value[0] as MemberRole
    }
  } catch (error) {
    console.error(error)
    $q.notify({
      message: t('roles.errors.load'),
      type: 'negative',
    })
  }
})
</script>
